import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, UserPlus, Receipt, UserCheck, MessageCircle, ChevronDown } from 'lucide-react';
import { cn } from '@techsofit/ui';

interface QuickAction {
  label: string;
  icon: typeof Plus;
  to: string;
}

// The receptionist's daily verbs. All are front-desk tasks, so staff and owners alike see them.
// "Send WhatsApp" has no standalone composer — nudges are per-member — so it opens Members to
// pick the recipient.
const ACTIONS: QuickAction[] = [
  { label: 'New member', icon: UserPlus, to: '/members/new' },
  { label: 'Record payment', icon: Receipt, to: '/payments/new' },
  { label: 'Check in', icon: UserCheck, to: '/attendance' },
  { label: 'Send WhatsApp', icon: MessageCircle, to: '/members' },
];

const MENU_WIDTH = 224; // w-56

/**
 * The quick-actions launcher: a "+ New" control that drops a menu of the common front-desk
 * tasks. The menu is portalled to <body> with fixed positioning so it's never clipped by the
 * sidebar, and flips above the trigger when there's no room below. `variant` picks the trigger:
 * a full-width button for the sidebar, a compact icon for the mobile top bar. `onNavigate` runs
 * before navigating (used to close the mobile drawer).
 */
export function QuickActions({
  variant,
  onNavigate,
  className,
}: {
  variant: 'button' | 'icon';
  onNavigate?: () => void;
  /** Extra classes for the trigger — e.g. `w-full` in the sidebar, `hidden md:inline-flex` in a header. */
  className?: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function place() {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const estHeight = ACTIONS.length * 44 + 8;
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow < estHeight + 8 && r.top > estHeight ? r.top - estHeight - 4 : r.bottom + 4;
    // Icon triggers sit at a right edge, so align the menu's right to the trigger; the sidebar
    // button aligns to its left. Either way clamp inside the viewport.
    const rawLeft = variant === 'icon' ? r.right - MENU_WIDTH : r.left;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - MENU_WIDTH - 8));
    setPos({ top, left });
  }

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt) || menuRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  function go(to: string) {
    setOpen(false);
    onNavigate?.();
    navigate(to);
  }

  return (
    <>
      {variant === 'button' ? (
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'inline-flex items-center justify-center gap-2 rounded-control bg-accent px-4 py-2.5 text-sm font-semibold text-accent-ink shadow-card transition-[filter] hover:brightness-95 active:scale-[0.99]',
            className,
          )}
        >
          <Plus className="size-4" aria-hidden />
          New
          <ChevronDown
            className={cn('size-4 transition-transform duration-200', open ? 'rotate-180' : '')}
            aria-hidden
          />
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          aria-label="Quick actions"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex size-touch items-center justify-center rounded-control bg-accent text-accent-ink shadow-card active:scale-95"
        >
          <Plus className="size-5" aria-hidden />
        </button>
      )}

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="z-[60] overflow-hidden rounded-card border border-line bg-card p-1 shadow-pop animate-pop-in"
          >
            {ACTIONS.map((action) => (
              <button
                key={action.to + action.label}
                role="menuitem"
                type="button"
                onClick={() => go(action.to)}
                className="flex w-full items-center gap-2.5 rounded-control px-3 py-2.5 text-left text-sm font-medium text-ink-soft transition-colors hover:bg-surface hover:text-ink"
              >
                <action.icon className="size-4 shrink-0 text-muted" aria-hidden />
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
